// DESIGN-SPEC §5.9 TabBar 底部导航
"use client";

import { House, User, Candy } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { cn } from "@/lib/cn";
import { MoleculeIcon, TeaLeafIcon } from "./icons";

const items = [
  { href: "/", label: "首页", icon: House },
  { href: "/tea", label: "古树茶", icon: TeaLeafIcon },
  { href: "/hirudin", label: "水蛭素", icon: MoleculeIcon },
  { href: "/jiuyuejie", label: "汣悦解", icon: Candy },
  { href: "/member", label: "我的", icon: User },
] as const;

export function TabBar({
  staticMode = false,
  activeHref,
  className,
}: {
  staticMode?: boolean;
  activeHref?: string;
  className?: string;
}) {
  const pathname = usePathname();
  const [demoPath, setDemoPath] = useState(activeHref ?? "/");
  const currentPath = staticMode ? demoPath : (activeHref ?? pathname);

  return (
    <nav
      aria-label="主要导航"
      className={cn(
        "z-50 flex border-t border-line/80 bg-card/94 pb-[env(safe-area-inset-bottom)] shadow-[0_-10px_30px_rgba(16,40,27,0.09)] backdrop-blur-xl",
        staticMode
          ? "relative w-full"
          : "mobile-tab-bar fixed bottom-0 left-0 w-full",
        className,
      )}
    >
      {items.map((item) => {
        const isActive =
          item.href === "/"
            ? currentPath === "/"
            : currentPath === item.href || currentPath.startsWith(`${item.href}/`);
        const Icon = item.icon;
        const activeTextClass =
          item.href === "/tea"
            ? "text-tea"
            : item.href === "/hirudin"
              ? "text-hirudin"
              : "text-forest-700";
        const activeIconClass =
          item.href === "/tea"
            ? "bg-tea-bg"
            : item.href === "/hirudin"
              ? "bg-hirudin-bg"
              : "bg-forest-100";
        const activeRuleClass =
          item.href === "/tea"
            ? "bg-tea"
            : item.href === "/hirudin"
              ? "bg-hirudin"
              : "bg-forest-700";

        return (
          <Link
            key={item.href}
            href={item.href}
            onNavigate={(event) => {
              if (!staticMode) return;
              event.preventDefault();
              setDemoPath(item.href);
            }}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "relative flex h-[62px] flex-1 flex-col items-center justify-center gap-1 text-[10px] leading-3 text-ink-3 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-forest-500",
              isActive && `font-semibold ${activeTextClass}`,
            )}
          >
            {isActive ? (
              <span aria-hidden="true" className={cn("absolute top-0 h-0.5 w-8 rounded-b-full", activeRuleClass)} />
            ) : null}
            <span className={cn("flex h-7 min-w-10 items-center justify-center rounded-full transition-colors", isActive && activeIconClass)}>
              <Icon className="size-5 stroke-[1.6]" aria-hidden="true" />
            </span>
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
